import { Editor } from "@tiptap/react";
import store from "@/redux/store";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import { parseChartForExport } from "../utils/chartExportUtils";
import { withoutLinkContext } from "@/services/citationDeepLink";

const copyToClipboard = async (
  editor: Editor,
  format = "html",
  includeLinks: boolean = true,
  workspaceId?: string
) => {
  const jsonContent = editor.getJSON();

  // Determine base path for citation links
  const isReport = window.location.pathname.startsWith("/reports/");
  const basePath = isReport ? "/reports" : "/chat";

  const buildCitationUrl = (
    qid: string,
    cid: string,
    match?: string
  ): string => {
    let url = `${window.location.origin}${basePath}/${workspaceId}`;
    url += `?qid=${qid}&cid=${cid}`;
    if (match) {
      url += `&match=${encodeURIComponent(match)}`;
    }
    return url;
  };

  const captureChartImage = async (chartElement: HTMLElement) => {
    if (!chartElement) return null;

    const legendElements = chartElement.querySelectorAll(
      '[elementType="legend"]'
    );
    const originalStyles: {
      element: HTMLElement;
      backgroundColor: string;
      border: string;
      borderLeft: string;
    }[] = [];

    legendElements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      originalStyles.push({
        element: htmlEl,
        backgroundColor: htmlEl.style.backgroundColor,
        border: htmlEl.style.border,
        borderLeft: htmlEl.style.borderLeft,
      });
      htmlEl.style.backgroundColor = "transparent";
      htmlEl.style.border = "none";
      htmlEl.style.borderLeft = "none";
    });

    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(chartElement, {
      scale: 3,
      useCORS: true,
      logging: true,
    });

    originalStyles.forEach((style) => {
      style.element.style.backgroundColor = style.backgroundColor;
      style.element.style.border = style.border;
      style.element.style.borderLeft = style.borderLeft;
    });

    return canvas.toDataURL("image/png");
  };

  const processNode = async (
    node: any,
    format: string,
    content?: any[],
    index?: number
  ) => {
    switch (node.type) {
      case "text":
        let text = node.text;
        // Remove trailing space if next node is a citation
        // Citation nodes are always removed, other types only when includeLinks is false
        if (content && index !== undefined && index < content.length - 1) {
          const next = content[index + 1];
          const isCitationType =
            next &&
            (next.type === "numeric-citation" ||
              next.type === "editor-highlight-number" ||
              next.type === "query-info" ||
              next.type === "citation");

          const shouldCheck =
            next?.type === "citation" || (isCitationType && !includeLinks);

          if (shouldCheck) {
            // Check what comes after the citation
            const afterCitation =
              index + 1 < content.length - 1 ? content[index + 2] : null;
            const afterCitationText =
              afterCitation && afterCitation.type === "text"
                ? afterCitation.text
                : "";

            // Check if citation is followed by punctuation
            const isFollowedByPunctuation =
              afterCitationText &&
              /^[.,;:!?)\]}]/.test(afterCitationText.trim());

            // Check if text after citation starts with space (would create double space)
            const afterCitationStartsWithSpace =
              afterCitationText && /^\s/.test(afterCitationText);

            // Remove trailing space if citation is followed by punctuation or would create double space
            if (isFollowedByPunctuation || afterCitationStartsWithSpace) {
              text = text.replace(/\s+$/, "");
            }
          }
        }
        if (node.marks && format !== "plain") {
          const linkMark = node.marks.find((mark: any) => mark.type === "link");
          node.marks.forEach((mark: any) => {
            switch (mark.type) {
              case "bold":
                text =
                  format === "html"
                    ? `<strong>${text}</strong>`
                    : `**${text}**`;
                break;
              case "italic":
                text = format === "html" ? `<em>${text}</em>` : `*${text}*`;
                break;
            }
          });
          if (linkMark && linkMark.attrs?.href && includeLinks) {
            // A link leaving the app carries no passage text (#14039).
            let href = withoutLinkContext(linkMark.attrs.href);
            if (href.startsWith("/")) {
              href = `${window.location.origin}${href}`;
            }
            text =
              format === "html"
                ? `<a href="${href}">${text}</a>`
                : `[${text}](${href})`;
          }
        }
        return text;
      case "numeric-citation": {
        if (node.content) {
          const numValue = node.content
            .map((child: any) => child.text || "")
            .join("");
          if (!includeLinks) {
            return numValue;
          }
          if (workspaceId && node.attrs?.qid && node.attrs?.cids) {
            const url = buildCitationUrl(
              node.attrs.qid,
              node.attrs.cids,
              node.attrs.match
            );
            return format === "html"
              ? `<a href="${url}">${numValue}</a>`
              : `[${numValue}](${url})`;
          }
          return `${numValue}`;
        }
        return "";
      }
      case "editor-highlight-number": {
        if (node.attrs && node.attrs.numValue) {
          const numValue =
            node.attrs.originalNumValue || node.attrs.numValue?.toString();
          if (!includeLinks) {
            return numValue;
          }
          if (workspaceId && node.attrs.qid) {
            const ehCid = node.attrs.numValue?.toString();
            const ehMatch = node.attrs.originalNumValue || numValue;
            const url = buildCitationUrl(node.attrs.qid, ehCid, ehMatch);
            return format === "html"
              ? `<a href="${url}">${numValue}</a>`
              : `[${numValue}](${url})`;
          }
          return `${numValue}`;
        }
        return "";
      }
      case "query-info":
        if (!includeLinks) return "";
        // Query info icons are typically not included in clipboard exports
        return "";
      case "citation":
        // Citation nodes are always removed (they're just visual markers)
        return "";
      case "paragraph":
        const paragraphContent = await processContent(node.content, format);
        return format === "html"
          ? `<p>${paragraphContent}</p>`
          : format === "markdown"
            ? `${paragraphContent}\n\n`
            : `${paragraphContent}\n`;
      case "bulletList":
        const bulletListContent = await processContent(node.content, format);
        return format === "html"
          ? `<ul>${bulletListContent}</ul>`
          : format === "markdown"
            ? `${bulletListContent}\n`
            : `${bulletListContent}`;
      case "orderedList":
        const orderedListContent = await processContent(node.content, format);
        return format === "html"
          ? `<ol>${orderedListContent}</ol>`
          : format === "markdown"
            ? `${orderedListContent}\n`
            : `${orderedListContent}`;
      case "listItem":
        const listItemContent = await processContent(node.content, format);
        return format === "html"
          ? `<li>${listItemContent}</li>`
          : format === "markdown"
            ? `- ${listItemContent}\n`
            : `• ${listItemContent}\n`;
      case "table":
        if (!node.content) return "";
        return parseTable(node, format, includeLinks, buildCitationUrl);
      case "heading":
        const headingContent = await processContent(node.content, format);
        return format === "html"
          ? `<h${node.attrs.level}>${headingContent}</h${node.attrs.level}>`
          : format === "markdown"
            ? `${"#".repeat(node.attrs.level)} ${headingContent}\n\n`
            : `${headingContent.toUpperCase()}\n`;
      case "blockquote":
        const blockquoteContent = await processContent(node.content, format);
        return format === "html"
          ? `<blockquote>${blockquoteContent}</blockquote>`
          : format === "markdown"
            ? `> ${blockquoteContent}\n\n`
            : `"${blockquoteContent}"\n`;
      case "codeBlock":
        const codeContent = await processContent(node.content, format);
        return format === "html"
          ? `<pre><code>${codeContent}</code></pre>`
          : format === "markdown"
            ? `\`\`\`\n${codeContent}\n\`\`\`\n\n`
            : `${codeContent}\n`;
      case "queryTextBox":
        if (!node.content) return "";
        const queryBoxContent = await processContent(node.content, format);
        return format === "html"
          ? `<p><em>${queryBoxContent}</em></p>`
          : format === "markdown"
            ? `*${queryBoxContent}*\n\n`
            : `${queryBoxContent}\n`;
      case "chart-node": {
        const { qid, id: chartId } = node.attrs || {};
        if (!qid || !chartId) return "";

        try {
          const queryAnswers = store.getState().fpAppV4Data.queryAnswers || [];
          const queryAnswer = queryAnswers.find((qa: any) => qa.qid === qid);
          const chartData =
            queryAnswer?.toolResponse?.answer?.chartData?.[chartId];
          if (!chartData) return "";

          const processedChartData = parseChartForExport(chartData);
          if (
            !processedChartData ||
            typeof processedChartData !== "object" ||
            Array.isArray(processedChartData)
          )
            return "";

          const FULL_WIDTH_PX = 800;
          const renderWidth = node.attrs.width
            ? Math.round((node.attrs.width / 100) * FULL_WIDTH_PX)
            : FULL_WIDTH_PX;
          const config = processedChartData?.charts?.[0] ?? processedChartData;
          const ar = config?.options?.aspectRatio;
          let renderHeight: number;
          if (ar != null) {
            renderHeight = Math.round(renderWidth / ar);
          } else if (node.attrs.height != null) {
            renderHeight = node.attrs.height;
          } else {
            renderHeight = 600;
          }

          const response = await fpFetch(
            `${BASE_API_URL_V2}/api/v2/chat/render-chart`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                chart: processedChartData,
                width: renderWidth,
                height: renderHeight,
              }),
            }
          );

          if (!response.ok) return "";

          const renderResult = await response.json();
          const imageBase64 = renderResult.imageBase64;
          if (!imageBase64) return "";

          return format === "html"
            ? `<img src="data:image/png;base64,${imageBase64}" alt="Chart" style="max-width: 100%; height: auto;">`
            : format === "markdown"
              ? `![Chart](data:image/png;base64,${imageBase64})\n\n`
              : "[Chart Image]\n";
        } catch {
          return "";
        }
      }
      case "MarketData":
        const chartElement = document.querySelector(
          `[data-qid="${node.attrs.qid}"]`
        );
        if (chartElement) {
          const chartImage = await captureChartImage(
            chartElement as HTMLElement
          );
          if (chartImage) {
            return format === "html"
              ? `<img src="${chartImage}" alt="Chart" style="max-width: 100%; height: auto;">`
              : format === "markdown"
                ? `![Chart](${chartImage})\n\n`
                : "[Chart Image]\n";
          } else {
            console.warn("Failed to capture chart image");
          }
        } else {
          console.warn("Chart element not found for MarketData node");
        }
        return "";
      default:
        return "";
    }
  };

  const processContent = async (content: any, format: string) => {
    if (!content) return "";
    const processedNodes = await Promise.all(
      content.map((node: any, index: number) =>
        processNode(node, format, content, index)
      )
    );
    return processedNodes.join("");
  };

  try {
    const htmlContent = await processContent(jsonContent.content, "html");
    const plainTextContent = await processContent(jsonContent.content, "plain");
    const markdownContent = await processContent(
      jsonContent.content,
      "markdown"
    );

    // Use the Clipboard API to write both HTML and plain text
    if (navigator.clipboard && navigator.clipboard.write) {
      const clipboardItem = new ClipboardItem({
        "text/html": new Blob([htmlContent], { type: "text/html" }),
        "text/plain": new Blob(
          [format === "markdown" ? markdownContent : plainTextContent],
          { type: "text/plain" }
        ),
      });

      await navigator.clipboard.write([clipboardItem]);
      return "success";
    } else {
      // Fallback to only copying plain text if Clipboard API is not fully supported
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(plainTextContent);
        return "success";
      } else {
        throw new Error("Clipboard API not supported");
      }
    }
  } catch (err) {
    console.error("Failed to copy to clipboard:", err);
    throw err;
  }
};

const parseTable = (
  node: any,
  format = "html",
  includeLinks: boolean = true,
  buildCitationUrl?: (qid: string, cid: string, match?: string) => string
) => {
  // Helper function to process cell content
  function processCellContent(content: any) {
    if (!content) return "";

    return content
      .map((item: any) => {
        if (item.type === "editor-highlight-number") {
          const numValue =
            item.attrs.originalNumValue || item.attrs.numValue?.toString();
          if (
            includeLinks &&
            buildCitationUrl &&
            item.attrs.qid &&
            item.attrs.numValue
          ) {
            const url = buildCitationUrl(
              item.attrs.qid,
              item.attrs.numValue.toString(),
              item.attrs.originalNumValue || numValue
            );
            return format === "html"
              ? `<a href="${url}">${numValue}</a>`
              : `[${numValue}](${url})`;
          }
          return numValue;
        } else if (item.type === "numeric-citation") {
          // Extract numeric value from numeric-citation node
          if (item.content) {
            const numValue = item.content
              .map((child: any) => child.text || "")
              .join("");
            if (
              includeLinks &&
              buildCitationUrl &&
              item.attrs?.qid &&
              item.attrs?.cids
            ) {
              const url = buildCitationUrl(
                item.attrs.qid,
                item.attrs.cids,
                item.attrs.match
              );
              return format === "html"
                ? `<a href="${url}">${numValue}</a>`
                : `[${numValue}](${url})`;
            }
            return numValue;
          }
          return "";
        } else if (item.type === "text") {
          let text = item.text;
          if (includeLinks && item.marks) {
            const linkMark = item.marks.find(
              (mark: any) => mark.type === "link"
            );
            if (linkMark && linkMark.attrs?.href) {
              // A link leaving the app carries no passage text (#14039).
              let href = withoutLinkContext(linkMark.attrs.href);
              if (href.startsWith("/")) {
                href = `${window.location.origin}${href}`;
              }
              return format === "html"
                ? `<a href="${href}">${text}</a>`
                : `[${text}](${href})`;
            }
          }
          return text;
        } else if (item.type === "paragraph" && item.content) {
          return processCellContent(item.content);
        }
        return "";
      })
      .join("");
  }

  // Process each row of the table
  const rows = node.content.map((row: any, rowIndex: number) => {
    const cells = row.content.map((cell: any) => {
      const cellContent = processCellContent(cell.content);

      if (format === "html") {
        const tag = cell.type === "tableHeader" ? "th" : "td";
        return `<${tag}>${cellContent}</${tag}>`;
      } else if (format === "markdown") {
        // Escape pipes for markdown
        return cellContent.replace(/\|/g, "\\|");
      }
      return cellContent;
    });

    if (format === "html") {
      return `<tr>${cells.join("")}</tr>`;
    } else if (format === "markdown") {
      return `| ${cells.join(" | ")} |`;
    }
    return cells.join("\t");
  });

  if (format === "html") {
    // First row is header in thead, rest in tbody
    return `<table>
      <thead>${rows[0]}</thead>
      <tbody>${rows.slice(1).join("")}</tbody>
    </table>`;
  } else if (format === "markdown") {
    // Create separator row for markdown
    const columnCount = node.content[0]?.content?.length || 0;
    const separator = `| ${Array(columnCount).fill("---").join(" | ")} |`;

    return `${rows[0]}\n${separator}\n${rows.slice(1).join("\n")}`;
  }

  // Plain text format
  return rows.join("\n");
};

export default copyToClipboard;
